import React from 'react';
import { Helmet } from 'react-helmet-async';
import { AuthSkyline } from '../components/common/AuthSkyline';
import { NAVIGATION_HEIGHT } from '../constants/global';

const PrivacyPolicy = () => (
  <>
    <Helmet>
      <title>Privacy Policy - tute</title>
      <meta name="description" content="Privacy policy for tute.academy — Oxford & Cambridge tutoring platform." />
    </Helmet>
    <div style={{ position: 'relative', marginTop: NAVIGATION_HEIGHT }}>
      <AuthSkyline extraLight fixed />
      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '2rem 1.5rem 4rem', position: 'relative', zIndex: 1 }}>

      <h1 style={{ fontSize: '2rem', fontWeight: 700, color: '#0f172a', margin: '0 0 0.5rem' }}>Privacy Policy</h1>
      <p style={{ color: '#64748b', fontSize: '0.9rem', margin: '0 0 2rem' }}>Last updated: 12 April 2026</p>

      <Section title="1. Who we are">
        tute ("we", "us", "our") operates the website <strong>tute.academy</strong>, a platform that connects parents with verified Oxford and Cambridge university tutors. Our registered contact email is <strong>patrickborman16@gmail.com</strong>.
      </Section>

      <Section title="2. What data we collect">
        <ul style={{ paddingLeft: '1.25rem', margin: '0.5rem 0' }}>
          <li><strong>Account information:</strong> name, email address, password (hashed), role (tutor or parent).</li>
          <li><strong>Profile information:</strong> university, college, degree course, qualifications, subjects, teaching levels, availability, profile photo, CV (tutors only), description.</li>
          <li><strong>Payment information:</strong> processed securely by Stripe. We do not store card numbers.</li>
          <li><strong>Messages:</strong> messages exchanged between tutors and parents on the platform.</li>
          <li><strong>Lesson data:</strong> lesson times, subjects, frequency, and status.</li>
          <li><strong>Google Calendar data:</strong> if you choose to connect Google Calendar, we access your calendar solely to create and manage lesson events. We do not read, modify, or delete any other calendar data.</li>
        </ul>
      </Section>

      <Section title="3. How we use Google Calendar data">
        When a tutor connects their Google Calendar account, we use the Google Calendar API <strong>exclusively</strong> to:
        <ul style={{ paddingLeft: '1.25rem', margin: '0.5rem 0' }}>
          <li>Create calendar events for booked lessons.</li>
          <li>Check availability to prevent double-booking.</li>
        </ul>
        We do <strong>not</strong>:
        <ul style={{ paddingLeft: '1.25rem', margin: '0.5rem 0' }}>
          <li>Read or access any existing calendar events beyond availability checks.</li>
          <li>Share calendar data with any third party.</li>
          <li>Use calendar data for advertising or any purpose other than lesson scheduling.</li>
          <li>Store calendar data beyond what is necessary for the lesson booking.</li>
        </ul>
        You can disconnect Google Calendar at any time from your profile settings.
      </Section>

      <Section title="4. Legal basis for processing">
        We process your data on the following grounds:
        <ul style={{ paddingLeft: '1.25rem', margin: '0.5rem 0' }}>
          <li><strong>Contract:</strong> to provide the tutoring platform services you signed up for.</li>
          <li><strong>Consent:</strong> for optional features like Google Calendar integration.</li>
          <li><strong>Legitimate interest:</strong> to improve our platform and prevent fraud.</li>
        </ul>
      </Section>

      <Section title="5. How we store and protect your data">
        Your data is stored securely using <strong>Supabase</strong> (PostgreSQL database hosted on AWS). Passwords are hashed using bcrypt. Authentication uses JWT tokens with short-lived access tokens (60 seconds) and refresh tokens (20 minutes) stored in httpOnly cookies. All connections use HTTPS/TLS encryption.
      </Section>

      <Section title="6. Third-party services">
        We use the following third-party services:
        <ul style={{ paddingLeft: '1.25rem', margin: '0.5rem 0' }}>
          <li><strong>Supabase:</strong> database and file storage.</li>
          <li><strong>Stripe:</strong> payment processing.</li>
          <li><strong>Google Calendar API:</strong> lesson event creation (with your consent).</li>
          <li><strong>Resend:</strong> transactional emails (verification, password reset).</li>
          <li><strong>Vercel:</strong> hosting.</li>
        </ul>
        We do not sell your data to any third party.
      </Section>

      <Section title="7. Your rights">
        You have the right to:
        <ul style={{ paddingLeft: '1.25rem', margin: '0.5rem 0' }}>
          <li>Access the personal data we hold about you.</li>
          <li>Correct any inaccurate data.</li>
          <li>Request deletion of your account and data.</li>
          <li>Withdraw consent for Google Calendar access at any time.</li>
          <li>Export your data.</li>
        </ul>
        To exercise any of these rights, contact us at <strong>patrickborman16@gmail.com</strong>.
      </Section>

      <Section title="8. Data retention">
        We retain your data for as long as your account is active. If you delete your account, we will remove your personal data within 30 days, except where we are required by law to retain it.
      </Section>

      <Section title="9. Cookies">
        We use only essential cookies required for authentication (JWT refresh token). We do not use advertising or tracking cookies.
      </Section>

      <Section title="10. Changes to this policy">
        We may update this policy from time to time. We will notify registered users of significant changes via email.
      </Section>

      <Section title="11. Contact">
        If you have questions about this privacy policy, please contact us at <strong>patrickborman16@gmail.com</strong>.
      </Section>
    </div>
    </div>
  </>
);

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div style={{ marginBottom: '2rem' }}>
    <h2 style={{ fontSize: '1.15rem', fontWeight: 600, color: '#0f172a', margin: '0 0 0.5rem' }}>{title}</h2>
    <div style={{ color: '#334155', fontSize: '0.95rem', lineHeight: 1.7 }}>{children}</div>
  </div>
);

export default PrivacyPolicy;
